<template>
    <div id="new-appstore-channel-form">
        <a-form>
            <div class="form-wrapper">
                <div class="input-wrapper">
                    <label>{{ $t("appStore.channelName") }}</label>
                    <a-form-item
                        :validate-status="getErrorStatus('channelName')"
                        :help="getErrorMessage('channelName')"
                    >
                        <a-input
                            v-model="form.channelName"
                            :placeholder="$t('appStore.channelName')"
                            name="channelName"
                            ref="channelName"
                        />
                    </a-form-item>
                </div>

                <div class="credentials-section">
                    <div class="section-title">
                        <i class="material-icons">key</i>
                        {{ $t("appStore.credentials") }}
                    </div>
                    <div class="help-text">
                        {{ $t("appStore.credentialsHelp") }}
                        <a href="https://appstoreconnect.apple.com/access/api" target="_blank">
                            App Store Connect API
                        </a>
                    </div>

                    <div class="input-wrapper">
                        <label>{{ $t("appStore.issuerId") }}</label>
                        <a-form-item
                            :validate-status="getErrorStatus('issuerId')"
                            :help="getErrorMessage('issuerId')"
                        >
                            <a-input
                                v-model="form.issuerId"
                                :placeholder="$t('appStore.issuerIdPlaceholder')"
                                name="issuerId"
                            />
                        </a-form-item>
                    </div>

                    <div class="input-wrapper">
                        <label>{{ $t("appStore.keyId") }}</label>
                        <a-form-item
                            :validate-status="getErrorStatus('keyId')"
                            :help="getErrorMessage('keyId')"
                        >
                            <a-input
                                v-model="form.keyId"
                                :placeholder="$t('appStore.keyIdPlaceholder')"
                                name="keyId"
                            />
                        </a-form-item>
                    </div>

                    <div class="input-wrapper">
                        <label>{{ $t("appStore.privateKey") }}</label>
                        <a-form-item
                            :validate-status="getErrorStatus('privateKey')"
                            :help="getErrorMessage('privateKey')"
                        >
                            <a-textarea
                                v-model="form.privateKey"
                                :placeholder="$t('appStore.privateKeyPlaceholder')"
                                name="privateKey"
                                :rows="6"
                            />
                        </a-form-item>
                    </div>
                </div>

                <div class="connect-body">
                    <div>
                        <a-checkbox :checked="termOfService" @change="acceptTermOfService('pp')"></a-checkbox>
                        <b
                            >{{ $t("search.integration.readAndAgree") }}
                            <a @click="acceptTermOfService('pp')">{{ $t("search.integration.privatePolicy") }}</a>
                            {{ $t("message.and") }}
                            <a @click="acceptTermOfService('tac')">{{
                                $t("search.integration.termsAndCondition")
                            }}</a></b
                        >
                    </div>
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <div class="actions">
                <SupsisButton ghost @click="handleCancel"> {{ $t("wp.cancel") }}</SupsisButton>
                <SupsisButton
                    @click="validateCredentials"
                    :disabled="!canValidate || validating"
                    :loading="validating"
                >
                    {{ $t("appStore.validateCredentials") }}
                </SupsisButton>
                <SupsisButton
                    primary
                    @click="handleSubmit"
                    :disabled="!termOfService || loading || !credentialsValidated"
                    :loading="loading"
                >
                    {{ $t("appStore.connectToAppStore") }}
                </SupsisButton>
            </div>
        </div>
        <TermOfService
            v-if="termOfServiceModal"
            :type="termOfServiceModal"
            @agree="acceptedTermOfService"
            @cancel="termOfServiceModal = null"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import TermOfService from "../../../../components/TermOfService.vue";
import { APP_STORE_API } from "../../../../api/appstore";

export default {
    name: "CreateAppStoreChannel",
    components: {
        TermOfService,
        SupsisButton,
    },
    data() {
        return {
            form: {
                channelName: "App Store Reviews",
                issuerId: "",
                keyId: "",
                privateKey: "",
            },
            loading: false,
            validating: false,
            submitClicked: false,
            termOfService: false,
            termOfServiceModal: false,
            credentialsValidated: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site && state.site.site._id,
        }),
        canValidate() {
            return this.form.issuerId && this.form.keyId && this.form.privateKey;
        },
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
            listChannels: "channel/listChannels",
        }),
        acceptTermOfService(type) {
            this.termOfServiceModal = type || "pp";
        },
        acceptedTermOfService() {
            this.termOfServiceModal = null;
            this.termOfService = true;
        },
        getErrorStatus(fieldName) {
            return !this.form[fieldName] && this.submitClicked ? "error" : "";
        },
        getErrorMessage(fieldName) {
            return !this.form[fieldName] && this.submitClicked ? this.$t("appStore.requiredField") : "";
        },
        handleCancel() {
            this.$emit("cancel");
        },
        async validateCredentials() {
            if (!this.canValidate) return;

            this.validating = true;
            this.credentialsValidated = false;

            try {
                const result = await APP_STORE_API.validateCredentials({
                    issuerId: this.form.issuerId,
                    keyId: this.form.keyId,
                    privateKey: this.form.privateKey,
                });

                if (result.data.success) {
                    this.$message.success(this.$t("appStore.credentialsValid"));
                    this.credentialsValidated = true;
                } else {
                    this.$message.error(result.data.error || this.$t("appStore.credentialsInvalid"));
                }
            } catch (err) {
                const errorMessage = err.response && err.response.data && err.response.data.error 
                    ? err.response.data.error 
                    : this.$t("appStore.credentialsInvalid");
                this.$message.error(errorMessage);
            } finally {
                this.validating = false;
            }
        },
        async handleSubmit() {
            this.submitClicked = true;
            if (!this.form.channelName || !this.form.issuerId || !this.form.keyId || !this.form.privateKey) {
                return;
            }

            if (!this.credentialsValidated) {
                this.$message.warning(this.$t("appStore.pleaseValidateFirst"));
                return;
            }

            this.loading = true;

            try {
                const channelData = {
                    type: "appstore-reviews",
                    name: this.form.channelName,
                    payload: {
                        issuerId: this.form.issuerId,
                        keyId: this.form.keyId,
                        privateKey: this.form.privateKey,
                    },
                };

                await this.createChannel(channelData);
                this.$message.success(this.$t("appStore.connectedSuccess"));
                this.listChannels();
                this.$emit("ok");
            } catch (err) {
                this.$message.error(this.$t("appStore.failedToCreateChannel"));
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
#new-appstore-channel-form {
    min-height: 250px;
    flex-direction: column;

    .form-wrapper {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;

            label {
                font-weight: 500;
            }
        }

        .credentials-section {
            margin-top: 10px;
            padding: 15px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            .section-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: bold;
                font-size: 16px;
                margin-bottom: 10px;

                .material-icons {
                    color: #007aff;
                }
            }

            .help-text {
                font-size: 13px;
                color: var(--color-text-muted);
                margin-bottom: 15px;

                a {
                    color: #007aff;
                }
            }
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        padding: 10px;
        margin-top: 20px;
        background: var(--color-bg-default);
        border-top: 1px solid var(--color-border-default);

        .actions {
            display: flex;
            gap: 10px;
        }
    }

    .connect-body {
        display: flex;
        flex-direction: column;
        gap: 5px;
        justify-content: center;
        margin-top: 10px;
    }
}
</style>
